import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  Heading,
  Badge,
  Tabs,
  TabList,
  TabItem,
  TabPanel,
  Spinner,
  Alert,
  Button,
  Divider,
  Dropdown,
  DropdownButton,
  DropdownOverlay,
  ActionList,
  ActionListItem,
} from '@razorpay/blade/components';
import { useQuery } from 'react-query';
import api from 'api';
import { Deductee, TDS_FILING_DATA_QUERY_KEYS } from 'api/tdsFilingData';
import { SalaryTable } from './SalaryTable';
import { ErrorTable } from './ErrorTable';
import { STATUS_BADGE_COLOR, STATUS_LABEL, paiseToRupees, formatEpochDate } from './utils';

// ── Helpers ───────────────────────────────────────────────────────────────────

const KeyValueRow = ({ label, value }: { label: string; value: string | number | null | undefined }) => (
  <Box display="flex" gap="spacing.4" paddingY="spacing.2">
    <Box width="220px" flexShrink={0}>
      <Text color="surface.text.gray.muted" size="small" weight="semibold">
        {label}
      </Text>
    </Box>
    <Text size="small">{value ?? '—'}</Text>
  </Box>
);

// ── Combined Challan + Deductee view ──────────────────────────────────────────

type ChallanDeducteeRowProps = { filingId: string; challanRecordNumber: number; challanId: string; challanData: Record<string, unknown> };

const ChallanDeducteeRow = ({
  filingId,
  challanId,
  challanRecordNumber,
  challanData,
}: ChallanDeducteeRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: [...TDS_FILING_DATA_QUERY_KEYS.deductees(filingId, challanId), 1],
    queryFn: () => api.tdsFilingData.getDeductees(filingId, { challan_id: challanId, page: 1, page_size: 500 }),
    enabled: expanded,
  });

  const deductees: Deductee[] = data?.items ?? [];

  return (
    <Box marginBottom="spacing.4" borderRadius="medium" backgroundColor="surface.background.gray.subtle" padding="spacing.4">
      {/* Challan summary row */}
      <Box display="flex" alignItems="center" gap="spacing.4" marginBottom="spacing.2">
        <Text weight="semibold" size="medium">Challan #{challanRecordNumber}</Text>
        <Text size="small" color="surface.text.gray.muted">
          BSR: {(challanData.bsr_code as string) ?? '—'}
        </Text>
        <Text size="small" color="surface.text.gray.muted">
          Date: {formatEpochDate(challanData.challan_date as number | null)}
        </Text>
        <Text size="small" color="surface.text.gray.muted">
          Bank Challan No: {(challanData.bank_challan_no as string) ?? '—'}
        </Text>
      </Box>

      <Box display="flex" gap="spacing.6" marginBottom="spacing.3">
        <Box>
          <Text size="xsmall" color="surface.text.gray.muted">TDS (Income Tax)</Text>
          <Text size="small" weight="semibold">{paiseToRupees(challanData.tds_income_tax as number)}</Text>
        </Box>
        <Box>
          <Text size="xsmall" color="surface.text.gray.muted">Surcharge</Text>
          <Text size="small" weight="semibold">{paiseToRupees(challanData.tds_surcharge as number)}</Text>
        </Box>
        <Box>
          <Text size="xsmall" color="surface.text.gray.muted">Interest</Text>
          <Text size="small" weight="semibold">{paiseToRupees(challanData.tds_interest as number)}</Text>
        </Box>
        <Box>
          <Text size="xsmall" color="surface.text.gray.muted">Total Deposit</Text>
          <Text size="small" weight="semibold">{paiseToRupees(challanData.total_deposit_amount as number)}</Text>
        </Box>
        <Box>
          <Text size="xsmall" color="surface.text.gray.muted">TDS Total</Text>
          <Text size="small" weight="semibold">{paiseToRupees(challanData.tds_total_amount as number)}</Text>
        </Box>
        <Box>
          <Text size="xsmall" color="surface.text.gray.muted">Deductees</Text>
          <Text size="small" weight="semibold">{challanData.deductee_count as number}</Text>
        </Box>
      </Box>

      {/* Deductees — always shown directly below challan (no button) */}
      {(challanData.deductee_count as number) > 0 && (() => {
        // Trigger load on first render
        if (!expanded) setExpanded(true);
        return null;
      })()}
      <Box marginTop="spacing.3">
        {isLoading ? (
          <Box display="flex" justifyContent="center" padding="spacing.4">
            <Spinner accessibilityLabel="Loading deductees" />
          </Box>
        ) : (
          <Box overflowX="auto">
            {/* Deductee table — native HTML for density */}
            <table
              style={{ borderCollapse: 'collapse', fontSize: '12px', width: '100%' }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: 'var(--surface-background-gray-moderate)',
                      color: 'var(--surface-text-gray-muted)',
                    }}
                  >
                    <th style={{ padding: '8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Line # (TXT)</th>
                    <th style={{ padding: '8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Record #</th>
                    <th style={{ padding: '8px', textAlign: 'left', whiteSpace: 'nowrap' }}>PAN</th>
                    <th style={{ padding: '8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Name</th>
                    <th style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap' }}>TDS Amount</th>
                    <th style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Surcharge</th>
                    <th style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Cess</th>
                    <th style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Total Tax</th>
                    <th style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Total Deposit</th>
                  </tr>
                </thead>
                <tbody>
                  {deductees.map((d: Deductee, i) => (
                    <tr
                      key={d.deductee_id}
                      style={{
                        borderBottom: '1px solid var(--surface-border-gray-muted)',
                        backgroundColor:
                          i % 2 === 0 ? 'transparent' : 'var(--surface-background-gray-moderate)',
                      }}
                    >
                      <td
                        style={{
                          padding: '6px 8px',
                          fontWeight: 'bold',
                          color: 'var(--interactive-text-primary-normal)',
                        }}
                      >
                        {d.line_number}
                      </td>
                      <td style={{ padding: '6px 8px', color: 'var(--surface-text-gray-muted)' }}>
                        {d.deductee_record_number}
                      </td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{d.pan}</td>
                      <td style={{ padding: '6px 8px' }}>{d.name}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{paiseToRupees(d.tds_amount)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{paiseToRupees(d.surcharge)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{paiseToRupees(d.cess)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{paiseToRupees(d.total_tax)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{d.total_deposit != null ? paiseToRupees(d.total_deposit) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data && data.total_count > 500 && (
                <Text size="xsmall" color="surface.text.gray.muted" marginTop="spacing.2">
                  Showing first 500 of {data.total_count} deductees.
                </Text>
              )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ChallanDeducteeTab = ({ filingId }: { filingId: string }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: [...TDS_FILING_DATA_QUERY_KEYS.challans(filingId), 'all'],
    queryFn: () => api.tdsFilingData.getChallans(filingId, { page: 1, page_size: 200 }),
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" padding="spacing.8">
        <Spinner accessibilityLabel="Loading challans" />
      </Box>
    );
  }
  if (isError) {
    return <Alert color="negative" description="Failed to load challan data." />;
  }

  const challans = data?.items ?? [];

  if (challans.length === 0) {
    return (
      <Box padding="spacing.6" textAlign="center">
        <Text color="surface.text.gray.muted">No challan data available for this filing.</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Text size="small" color="surface.text.gray.muted" marginBottom="spacing.4">
        {challans.length} challan{challans.length !== 1 ? 's' : ''}. Click "View Deductees" to expand employee details under each challan.
      </Text>
      {challans.map((c) => (
        <ChallanDeducteeRow
          key={c.challan_id}
          filingId={filingId}
          challanId={c.challan_id}
          challanRecordNumber={c.challan_record_number}
          challanData={c as unknown as Record<string, unknown>}
        />
      ))}
    </Box>
  );
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface TdsFilingDetailsProps {
  filingId: string | null;
  requestId?: string | null;
  onClose: () => void;
  onBack?: () => void; // If provided, shows a back button (came from version drawer)
}

// ── Main Component ────────────────────────────────────────────────────────────

export const TdsFilingDetails = ({
  filingId,
  requestId,
  onClose,
  onBack,
}: TdsFilingDetailsProps) => {
  const [activeTab, setActiveTab] = useState('header');
  const isOpen = !!filingId;

  const { data: filing, isLoading, isError } = useQuery({
    queryKey: TDS_FILING_DATA_QUERY_KEYS.details(filingId ?? ''),
    queryFn: () => api.tdsFilingData.getFilingDetails(filingId!),
    enabled: isOpen,
  });

  // Fetch error count to conditionally show Errors tab
  const { data: errorsData } = useQuery({
    queryKey: [...TDS_FILING_DATA_QUERY_KEYS.errors(filingId ?? ''), 'count'],
    queryFn: () => api.tdsFilingData.getErrors(filingId!, { page: 1, page_size: 1 }),
    enabled: isOpen && !!filing,
  });
  const hasErrors = (errorsData?.total_count ?? 0) > 0;

  const handleDownload = async (fileType: 'txt' | 'fvu' | '27A' | 'filing_ack') => {
    if (!filingId) return;
    try {
      const result = await api.tdsFilingData.getDownloadUrl(filingId, fileType, requestId ?? undefined);
      window.open(result.signed_url, '_blank');
    } catch {
      alert(`Failed to get download URL for ${fileType}. The file may not be available for this filing.`);
    }
  };

  const isQ4 = filing?.quarter === 'Q4';
  const isCompleted = filing?.status === 'completed';

  const handleClose = () => {
    setActiveTab('challan-deductees');
    onClose();
  };

  const handleBack = () => {
    setActiveTab('challan-deductees');
    onBack?.();
  };

  return (
    <Modal isOpen={isOpen} onDismiss={handleClose} size="full" accessibilityLabel="Filing details">
      <ModalHeader
        title={
          filing
            ? `${filing.filing_type} ${filing.quarter} ${filing.fy} — v${filing.version}`
            : 'Filing Details'
        }
        subtitle={
          filing
            ? `Org ${filing.organization_id ?? filing.merchant_id ?? '—'} • ${STATUS_LABEL[filing.status]}`
            : undefined
        }
      />
      <ModalBody>
        {isLoading && (
          <Box display="flex" justifyContent="center" padding="spacing.8">
            <Spinner accessibilityLabel="Loading filing details" />
          </Box>
        )}
        {isError && (
          <Alert color="negative" description="Failed to load filing details. Please try again." />
        )}

        {filing && !isLoading && (
          <Box>
            {/* Action bar: back button, status badge, download */}
            <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom="spacing.5">
              <Box display="flex" alignItems="center" gap="spacing.3">
                {onBack && (
                  <Button size="small" variant="tertiary" onClick={handleBack}>
                    ← Back to versions
                  </Button>
                )}
                <Badge color={STATUS_BADGE_COLOR[filing.status]} size="large">
                  {STATUS_LABEL[filing.status]}
                </Badge>
              </Box>
              {isCompleted && (
                <Dropdown>
                  <DropdownButton size="small" variant="secondary">
                    Download ▾
                  </DropdownButton>
                  <DropdownOverlay>
                    <ActionList>
                      <ActionListItem title={`${filing.filing_type} TXT File`} value="txt" onClick={() => handleDownload('txt')} />
                      <ActionListItem title="FVU File (filing workflow)" value="fvu" onClick={() => handleDownload('fvu')} />
                      <ActionListItem title="Form 27A" value="27A" onClick={() => handleDownload('27A')} />
                      <ActionListItem title="Filing Acknowledgment" value="filing_ack" onClick={() => handleDownload('filing_ack')} />
                    </ActionList>
                  </DropdownOverlay>
                </Dropdown>
              )}
            </Box>

            {/* Parse summary pills */}
            {isCompleted && filing.parse_summary && (
              <Box display="flex" gap="spacing.6" marginBottom="spacing.5" padding="spacing.4"
                backgroundColor="surface.background.primary.subtle" borderRadius="medium">
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">Challans</Text>
                  <Text weight="semibold">{filing.parse_summary.challan_count}</Text>
                </Box>
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">Deductees</Text>
                  <Text weight="semibold">{filing.parse_summary.deductee_count}</Text>
                </Box>
                {isQ4 && (
                  <Box>
                    <Text size="xsmall" color="surface.text.gray.muted">Salary Records</Text>
                    <Text weight="semibold">{filing.parse_summary.salary_count}</Text>
                  </Box>
                )}
                {filing.file_hash_sha256 && (
                  <Box>
                    <Text size="xsmall" color="surface.text.gray.muted">File SHA256</Text>
                    <code style={{ fontSize: '11px' }}>
                      {filing.file_hash_sha256.slice(0, 16)}…
                    </code>
                  </Box>
                )}
              </Box>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onChange={(v) => setActiveTab(v)}>
              <TabList>
                <TabItem value="header">File Header / Batch Header</TabItem>
                <TabItem value="challan-deductees">Challans (CD) &amp; Deductees (DD)</TabItem>
                {isQ4 && <TabItem value="salaries">Salaries (SD)</TabItem>}
                {hasErrors && (
                  <TabItem value="errors">
                    Errors
                    {errorsData && errorsData.total_count > 0 && (
                      <Badge color="negative" size="small" marginLeft="spacing.2">
                        {errorsData.total_count}
                      </Badge>
                    )}
                  </TabItem>
                )}
              </TabList>

              {/* File Header + Batch Header — shown first */}
              <TabPanel value="header">
                <Box paddingTop="spacing.4" display="flex" gap="spacing.8" flexWrap="wrap">
                  <Box flex={1} minWidth="300px">
                    <Heading size="small" marginBottom="spacing.3">File Header (FH)</Heading>
                    <KeyValueRow label="File Type" value={filing.fh_file_type} />
                    <KeyValueRow label="Upload Type" value={filing.fh_upload_type} />
                    <KeyValueRow label="Creation Date" value={filing.fh_file_creation_date} />
                    <KeyValueRow label="TAN" value={filing.fh_tan} />
                    <KeyValueRow label="Return Prep Utility" value={filing.fh_return_prep_utility} />
                    <KeyValueRow label="Line Number" value={filing.fh_line_number} />
                  </Box>
                  <Box flex={2} minWidth="400px">
                    <Heading size="small" marginBottom="spacing.3">Batch Header (BH)</Heading>
                    <KeyValueRow label="Form No" value={filing.bh_form_no} />
                    <KeyValueRow label="PAN (Deductor)" value={filing.bh_pan_deductor} />
                    <KeyValueRow label="Assessment Year" value={filing.bh_assessment_year} />
                    <KeyValueRow label="Financial Year" value={filing.bh_financial_year} />
                    <KeyValueRow label="Period" value={filing.bh_period} />
                    <Divider marginY="spacing.3" />
                    <KeyValueRow label="Employer Name" value={filing.bh_employer_name} />
                    <KeyValueRow label="Employer Branch" value={filing.bh_employer_branch} />
                    <KeyValueRow label="Employer Email" value={filing.bh_employer_email} />
                    <KeyValueRow label="Employer State" value={filing.bh_employer_state} />
                    <KeyValueRow label="Employer PIN" value={filing.bh_employer_pin} />
                    <KeyValueRow label="Deductor Type" value={filing.bh_deductor_type} />
                    <Divider marginY="spacing.3" />
                    <KeyValueRow label="Responsible Person" value={filing.bh_responsible_person_name} />
                    <KeyValueRow label="Designation" value={filing.bh_responsible_person_designation} />
                    <KeyValueRow label="Resp. PAN" value={filing.bh_responsible_person_pan} />
                    <KeyValueRow label="Resp. Email" value={filing.bh_responsible_person_email} />
                    <KeyValueRow label="Resp. Mobile" value={filing.bh_responsible_person_mobile} />
                    <Divider marginY="spacing.3" />
                    <KeyValueRow label="Batch Deposit Total" value={paiseToRupees(filing.bh_batch_deposit_total)} />
                    <KeyValueRow label="Challan Count" value={filing.bh_challan_count} />
                    <KeyValueRow label="Deductee Count" value={filing.bh_deductee_count} />
                    {isQ4 && <KeyValueRow label="Salary Record Count" value={filing.bh_salary_record_count} />}
                    <KeyValueRow label="Gross Total Income" value={paiseToRupees(filing.bh_gross_total_income)} />
                  </Box>
                </Box>
              </TabPanel>

              {/* Challans (CD) + Deductees (DD) combined */}
              <TabPanel value="challan-deductees">
                <Box paddingTop="spacing.4">
                  <ChallanDeducteeTab filingId={filing.filing_data_id} />
                </Box>
              </TabPanel>

              {/* Salaries (SD) — Q4 only */}
              {isQ4 && (
                <TabPanel value="salaries">
                  <Box paddingTop="spacing.4">
                    <SalaryTable filingId={filing.filing_data_id} />
                  </Box>
                </TabPanel>
              )}

              {/* Errors — only shown if filing has errors */}
              {hasErrors && (
                <TabPanel value="errors">
                  <Box paddingTop="spacing.4">
                    <ErrorTable filingId={filing.filing_data_id} />
                  </Box>
                </TabPanel>
              )}
            </Tabs>
          </Box>
        )}
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="flex-end">
          <Button variant="tertiary" onClick={handleClose}>Close</Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
